import { Button } from '@crowdex/ui/components/button';
import { Dialog, DialogContent, DialogTitle } from '@crowdex/ui/components/dialog';
import { Input } from '@crowdex/ui/components/input';
import { RadioGroup, RadioGroupItem } from '@crowdex/ui/components/radio-group';
import { Textarea } from '@crowdex/ui/components/textarea';
import type {
  PullRequestCockpit,
  PullRequestFeedback,
  PullRequestMutation,
  PullRequestReactionContent,
  PullRequestReviewThread,
} from '@crowdex/workspace-protocol/pull-request-cockpit';
import {
  pullRequestFeedbackPrompt,
  visiblePullRequestFeedback,
} from '@crowdex/workspace-protocol/pull-request-cockpit';
import { type ReactNode, useState } from 'react';

export type Mutation = Omit<PullRequestMutation, 'expectedHeadSha'>;

const REACTIONS: readonly [PullRequestReactionContent, string][] = [
  ['THUMBS_UP', '👍'],
  ['THUMBS_DOWN', '👎'],
  ['LAUGH', '😄'],
  ['HOORAY', '🎉'],
  ['CONFUSED', '😕'],
  ['HEART', '❤️'],
  ['ROCKET', '🚀'],
  ['EYES', '👀'],
];

export const openExternal = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

function relativeTime(value: string | null): string {
  if (!value) return '';
  const elapsed = Date.now() - Date.parse(value);
  if (!Number.isFinite(elapsed)) return value.slice(0, 10);
  const minutes = Math.max(0, Math.floor(elapsed / 60_000));
  if (minutes < 60) return minutes < 2 ? 'now' : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return days < 30 ? `${days}d` : value.slice(0, 10);
}

function PanelDialog({
  title,
  children,
  close,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  close(): void;
  wide?: boolean;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent
        className={`grid max-h-[90vh] gap-3 overflow-y-auto ${wide ? 'sm:max-w-xl' : 'sm:max-w-md'}`}
      >
        <DialogTitle className="type-card-title">{title}</DialogTitle>
        {children}
      </DialogContent>
    </Dialog>
  );
}

function DialogActions({
  cancel,
  confirm,
  disabled,
  onConfirm,
}: {
  cancel(): void;
  confirm: string;
  disabled: boolean;
  onConfirm(): void;
}) {
  return (
    <div className="flex justify-end gap-2">
      <Button onClick={cancel} variant="outline">
        Cancel
      </Button>
      <Button disabled={disabled} onClick={onConfirm}>
        {confirm}
      </Button>
    </div>
  );
}

function Reactions({ reactions }: { reactions: PullRequestCockpit['reactions'] }) {
  if (reactions.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      {reactions.map((reaction) => (
        <span className="rounded-sm border border-hairline px-1.5 py-0.5" key={reaction.emoji}>
          {reaction.emoji} {reaction.count}
        </span>
      ))}
    </div>
  );
}

function FeedbackCard({
  feedback,
  thread,
  mutate,
  prompt,
}: {
  feedback: PullRequestFeedback;
  thread: PullRequestReviewThread | null;
  mutate(input: Mutation): Promise<void>;
  prompt(text: string): void;
}) {
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  };
  return (
    <article className="grid gap-2 rounded-xl bg-card p-3 text-sm">
      <header className="type-meta flex items-center gap-2 text-muted-foreground">
        <strong className="truncate font-medium text-foreground">{feedback.author.login}</strong>
        {feedback.state && <span>{feedback.state.replace('_', ' ')}</span>}
        <span className="ml-auto shrink-0">{relativeTime(feedback.createdAt)}</span>
      </header>
      {feedback.path && (
        <p className="type-meta font-mono text-muted-foreground">
          {feedback.path}
          {feedback.line ? `:${feedback.line}` : ''}
        </p>
      )}
      <p className="whitespace-pre-wrap break-words leading-normal">
        {feedback.body || 'No comment body.'}
      </p>
      <Reactions reactions={feedback.reactions} />
      <div className="flex flex-wrap items-center gap-1.5">
        <Button
          onClick={() =>
            prompt(
              `Address this pull request review feedback:\n\n${feedback.author.login}: ${feedback.body}`,
            )
          }
          size="xs"
          variant="outline"
        >
          Fix
        </Button>
        {feedback.url && (
          <Button onClick={() => openExternal(feedback.url ?? '')} size="xs" variant="outline">
            Open
          </Button>
        )}
        {thread?.viewerCanResolve && !thread.isResolved && (
          <Button
            disabled={busy}
            onClick={() =>
              void submit(() => mutate({ action: 'resolve_thread', threadId: thread.id }))
            }
            size="xs"
            variant="outline"
          >
            Resolve
          </Button>
        )}
        {thread &&
          REACTIONS.map(([reaction, emoji]) => (
            <Button
              aria-label={`React ${emoji}`}
              disabled={busy}
              key={reaction}
              onClick={() =>
                void submit(() =>
                  mutate({ action: 'react_comment', commentId: feedback.id, reaction }),
                )
              }
              size="icon-xs"
              variant="ghost"
            >
              {emoji}
            </Button>
          ))}
      </div>
      {thread && !thread.isResolved && (
        <form
          className="flex items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (!reply.trim()) return;
            void submit(async () => {
              await mutate({ action: 'reply_thread', threadId: thread.id, body: reply.trim() });
              setReply('');
            });
          }}
        >
          <Textarea
            aria-label="Reply to thread"
            className="min-h-14"
            onChange={(event) => setReply(event.target.value)}
            placeholder="Reply…"
            value={reply}
          />
          <Button disabled={busy || !reply.trim()} size="sm" type="submit" variant="outline">
            Reply
          </Button>
        </form>
      )}
    </article>
  );
}

/** Every comment of the pull request, with replies, reactions and a new comment (⋯ › Comments). */
export function CommentsDialog({
  cockpit,
  working,
  mutate,
  prompt,
  close,
}: {
  cockpit: PullRequestCockpit;
  working: boolean;
  mutate(input: Mutation): Promise<void>;
  prompt(text: string): void;
  close(): void;
}) {
  const [comment, setComment] = useState('');
  const feedback = visiblePullRequestFeedback(cockpit);
  const count = feedback.threads.length + feedback.standalone.length;
  return (
    <PanelDialog close={close} title="Comments" wide>
      <Reactions reactions={cockpit.reactions} />
      {feedback.threads.map((thread) =>
        thread.comments.map((item) => (
          <FeedbackCard
            feedback={item}
            key={item.id}
            mutate={mutate}
            prompt={prompt}
            thread={thread}
          />
        )),
      )}
      {feedback.standalone.map((item) => (
        <FeedbackCard feedback={item} key={item.id} mutate={mutate} prompt={prompt} thread={null} />
      ))}
      {count === 0 && <p className="text-muted-foreground text-sm">No comments yet.</p>}
      <form
        className="flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (comment.trim())
            void mutate({ action: 'comment', body: comment.trim() }).then(() => setComment(''));
        }}
      >
        <Textarea
          aria-label="Comment on pull request"
          className="min-h-14"
          onChange={(event) => setComment(event.target.value)}
          placeholder="Add a comment…"
          value={comment}
        />
        <Button disabled={working || !comment.trim()} size="sm" type="submit" variant="outline">
          Comment
        </Button>
      </form>
      {count > 0 && (
        <Button
          className="justify-self-start"
          onClick={() => prompt(pullRequestFeedbackPrompt(cockpit))}
          size="sm"
          variant="outline"
        >
          Address the comments with the agent
        </Button>
      )}
    </PanelDialog>
  );
}

export function EditDialog({
  cockpit,
  working,
  mutate,
  close,
}: {
  cockpit: PullRequestCockpit;
  working: boolean;
  mutate(input: Mutation): Promise<void>;
  close(): void;
}) {
  const [title, setTitle] = useState(cockpit.pullRequest.title);
  const [body, setBody] = useState(cockpit.pullRequest.body);
  return (
    <PanelDialog close={close} title="Edit pull request">
      <label className="grid gap-1.5 text-xs" htmlFor="pull-request-edit-title">
        Title
        <Input
          id="pull-request-edit-title"
          onChange={(event) => setTitle(event.target.value)}
          value={title}
        />
      </label>
      <label className="grid gap-1.5 text-xs" htmlFor="pull-request-edit-body">
        Description
        <Textarea
          id="pull-request-edit-body"
          onChange={(event) => setBody(event.target.value)}
          value={body}
        />
      </label>
      <DialogActions
        cancel={close}
        confirm="Save"
        disabled={working || !title.trim()}
        onConfirm={() => void mutate({ action: 'edit', title: title.trim(), body })}
      />
    </PanelDialog>
  );
}

export function ReviewDialog({
  working,
  mutate,
  close,
}: {
  working: boolean;
  mutate(input: Mutation): Promise<void>;
  close(): void;
}) {
  const [body, setBody] = useState('');
  const [decision, setDecision] = useState<'approve' | 'comment' | 'request_changes'>('comment');
  return (
    <PanelDialog close={close} title="Submit review">
      <RadioGroup
        aria-label="Review decision"
        onValueChange={(value) => setDecision(value as typeof decision)}
        value={decision}
      >
        <RadioGroupItem value="comment">Comment</RadioGroupItem>
        <RadioGroupItem value="approve">Approve</RadioGroupItem>
        <RadioGroupItem value="request_changes">Request changes</RadioGroupItem>
      </RadioGroup>
      <Textarea
        aria-label="Review body"
        onChange={(event) => setBody(event.target.value)}
        placeholder="Review comment…"
        value={body}
      />
      <DialogActions
        cancel={close}
        confirm="Submit"
        disabled={working || (decision !== 'approve' && !body.trim())}
        onConfirm={() => void mutate({ action: 'submit_review', decision, body })}
      />
    </PanelDialog>
  );
}

export function ReviewersDialog({
  working,
  mutate,
  close,
}: {
  working: boolean;
  mutate(input: Mutation): Promise<void>;
  close(): void;
}) {
  const [reviewers, setReviewers] = useState('');
  return (
    <PanelDialog close={close} title="Request reviewers">
      <Input
        aria-label="Reviewer usernames"
        onChange={(event) => setReviewers(event.target.value)}
        placeholder="octocat, monalisa"
        value={reviewers}
      />
      <DialogActions
        cancel={close}
        confirm="Request"
        disabled={working || !reviewers.trim()}
        onConfirm={() =>
          void mutate({
            action: 'request_reviewers',
            reviewers: reviewers
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean),
          })
        }
      />
    </PanelDialog>
  );
}

/**
 * Merging now, or once checks and review allow it (auto-merge): both pick the merge method
 * first.
 */
export function MergeDialog({
  whenReady,
  working,
  mutate,
  close,
}: {
  whenReady: boolean;
  working: boolean;
  mutate(input: Mutation): Promise<void>;
  close(): void;
}) {
  const [method, setMethod] = useState<'merge' | 'squash'>('squash');
  return (
    <PanelDialog close={close} title={whenReady ? 'Merge when ready' : 'Merge pull request'}>
      {whenReady && (
        <p className="text-muted-foreground text-sm">
          The pull request merges by itself once its checks pass and its review is approved.
        </p>
      )}
      <RadioGroup
        aria-label="Merge method"
        onValueChange={(value) => setMethod(value as 'merge' | 'squash')}
        value={method}
      >
        <RadioGroupItem value="squash">Squash and merge</RadioGroupItem>
        <RadioGroupItem value="merge">Create a merge commit</RadioGroupItem>
      </RadioGroup>
      <DialogActions
        cancel={close}
        confirm={whenReady ? 'Turn on auto-merge' : 'Confirm merge'}
        disabled={working}
        onConfirm={() =>
          void mutate(
            whenReady ? { action: 'enable_auto_merge', method } : { action: 'merge', method },
          )
        }
      />
    </PanelDialog>
  );
}
